/**
 * BulkActionBar Component
 *
 * Appears when items are selected, shows available bulk actions
 * - Dynamic action buttons from backend configuration
 * - Select All / Clear Selection controls
 * - Confirmation dialogs for destructive actions
 * - Action execution via API
 */

import React from 'react';
import { Button, Space, Badge, Divider, Modal, message } from 'antd';
import {
  CheckSquareOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { BulkSelectConfig } from '@/api/types/udc.types';
import './BulkActionBar.css';

interface BulkActionBarProps {
  bulkSelect: BulkSelectConfig;
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onExecuteAction: (actionKey: string, action: any) => void;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  bulkSelect,
  totalCount,
  onSelectAll,
  onClearSelection,
  onExecuteAction,
}) => {
  // Don't show if selection is disabled or nothing is selected
  if (
    !bulkSelect?.enabled ||
    !bulkSelect.selected_count ||
    bulkSelect.selected_count === 0
  ) {
    return null;
  }

  // Get icon component by name
  const getIcon = (iconName?: string) => {
    // Map of icon names to icon components
    // This can be extended based on backend icon names
    const iconMap: Record<string, React.ReactNode> = {
      check: <CheckSquareOutlined />,
      close: <CloseOutlined />,
    };

    return iconMap[iconName || ''] || null;
  };

  // Handle action click
  const handleActionClick = (actionKey: string, action: any) => {
    // Check if confirmation is required
    if (action.confirmation) {
      Modal.confirm({
        title: action.label || 'Confirm Action',
        icon: <ExclamationCircleOutlined />,
        content:
          action.confirmation_message ||
          `Are you sure you want to execute this action on ${bulkSelect.selected_count} items?`,
        okText: 'Confirm',
        cancelText: 'Cancel',
        onOk: () => {
          onExecuteAction(actionKey, action);
        },
      });
    } else {
      // Execute directly without confirmation
      onExecuteAction(actionKey, action);
    }
  };

  return (
    <div className="bulk-action-bar">
      <div className="bulk-action-bar-content">
        <Space size="middle" wrap>
          {/* Selection count badge */}
          <div className="bulk-selection-count">
            <Badge count={bulkSelect.selected_count} showZero>
              <CheckSquareOutlined
                style={{ fontSize: 18, color: 'var(--primary-color)' }}
              />
            </Badge>
            <span className="selection-text">
              {bulkSelect.selected_count} selected
            </span>
          </div>

          <Divider type="vertical" />

          {/* Select all button */}
          {bulkSelect.selected_count < totalCount && (
            <>
              <Button size="small" type="link" onClick={onSelectAll}>
                Select all {totalCount}
              </Button>
              <Divider type="vertical" />
            </>
          )}

          {/* Dynamic action buttons from backend */}
          {bulkSelect.actions &&
            Object.entries(bulkSelect.actions).map(([key, action]) => {
              // Check if action is in available_commands
              const commandKey = key.replace('on_', '').toUpperCase();
              if (
                bulkSelect.available_commands &&
                !bulkSelect.available_commands.includes(commandKey)
              ) {
                return null;
              }

              // Check min/max selected constraints
              const isDisabled =
                (action.min_selected &&
                  bulkSelect.selected_count < action.min_selected) ||
                (action.max_selected &&
                  bulkSelect.selected_count > action.max_selected);

              return (
                <Button
                  key={key}
                  type="default"
                  size="small"
                  icon={getIcon(action.icon)}
                  onClick={() => handleActionClick(key, action)}
                  disabled={isDisabled}
                  title={
                    isDisabled
                      ? `This action requires ${action.min_selected ? `at least ${action.min_selected}` : `at most ${action.max_selected}`} selected items`
                      : action.label
                  }
                >
                  {action.label}
                </Button>
              );
            })}

          <Divider type="vertical" />

          {/* Clear selection */}
          <Button
            size="small"
            type="text"
            icon={<CloseOutlined />}
            onClick={onClearSelection}
            className="clear-selection-btn"
          >
            Clear
          </Button>
        </Space>
      </div>
    </div>
  );
};
